import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  FormResponse,
  FormStatus,
  FormItemType,
  FormItemResponse,
  FormItemContent,
  CreateFormRequest,
  UpdateFormRequest,
  AddFormItemRequest,
  ReorderItemsRequest
} from '@/api/generated-forms'
import { initApiService } from '@/services/init'
import { getGlobalT } from '@/i18n'
import { extractApiErrorMessage, handleApiError, logUnexpectedFailure, logUnhandledApiFailure } from '@/utils/errorHandling'

/**
 * Form item type for frontend use
 */
export interface FormItem {
  id: string
  position: number
  itemType: FormItemType
  question: {
    id: string
    text: string
    type: string
    required: boolean
    description?: string | null
    aiInstruction?: string | null
    options?: Array<{ key: string; label: string }> | null
    fieldCount?: number | null
  } | null
  content: FormItemContent | null
  createdAt: number
}

/**
 * Form type for frontend use
 */
export interface Form {
  id: string
  organizationId: string
  title: string
  description: string | null
  /** General instruction for the AI form fill; never printed or shown to the client (#3435). */
  aiInstruction: string | null
  status: FormStatus
  forAppointment: boolean
  forClient: boolean
  /** False keeps this form's submissions out of the client AI recap entirely (#3766). */
  includeInAiRecap: boolean
  itemCount: number
  createdAt: number
  updatedAt: number
}

/**
 * Detailed form type including items
 */
export interface FormDetail extends Form {
  items: FormItem[]
}

/**
 * The editable metadata of a form — what the create/edit modal submits.
 */
export interface FormMetadata {
  title: string
  description: string | null
  aiInstruction: string | null
  forAppointment: boolean
  forClient: boolean
  includeInAiRecap: boolean
}

/**
 * Map form status to Dutch label
 */
export const formStatusLabels: Record<FormStatus, string> = {
  DRAFT: 'Concept',
  ACTIVE: 'Actief',
  DEPRECATED: 'Verouderd'
}

/**
 * Forms Store
 *
 * Manages forms for the organization.
 *
 * `loading` and `error` describe the **list load** only — `FormsListView` hands
 * them to a `DataTable`, which renders each of them *instead of* the rows, so a
 * mutation writing there would blank the whole list (#3398, #3407). Everything
 * else either rethrows or returns a result carrying the message; every caller
 * owns a surface next to the action that failed (`useModalSubmit` for the form
 * modal, the error modal for the delete outcome, the detail views' own refs).
 */
export const useFormsStore = defineStore('forms', () => {
  const t = getGlobalT()

  const forms = ref<Form[]>([])
  /** True while {@link fetchForms} is in flight. */
  const loading = ref(false)
  /** Message of the last failed {@link fetchForms}. */
  const error = ref<string | null>(null)

  /**
   * Whether the organization has at least one (non-deleted) form.
   * Drives conditional visibility of forms navigation (#3020).
   * `null` until fetched; kept in sync by form mutations below.
   */
  const usesForms = ref<boolean | null>(null)

  /**
   * Convert backend FormResponse to frontend Form type
   */
  function mapFormResponse(response: FormResponse): Form {
    return {
      id: response.id,
      organizationId: response.organizationId,
      title: response.title,
      description: response.description || null,
      aiInstruction: response.aiInstruction || null,
      status: response.status,
      forAppointment: response.forAppointment,
      forClient: response.forClient,
      includeInAiRecap: response.includeInAiRecap,
      itemCount: response.items?.length ?? 0,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt
    }
  }

  /**
   * Convert backend FormItemResponse to frontend FormItem type
   */
  function mapFormItemResponse(response: FormItemResponse): FormItem {
    return {
      id: response.id,
      position: response.position,
      itemType: response.itemType,
      question: response.question
        ? {
            id: response.question.id,
            text: response.question.currentVersion.text,
            type: response.question.currentVersion.type,
            required: response.question.currentVersion.required,
            description: response.question.currentVersion.description || null,
            aiInstruction: response.question.currentVersion.aiInstruction || null,
            options: response.question.currentVersion.options || null,
            fieldCount: response.question.currentVersion.fieldCount ?? null
          }
        : null,
      content: response.content || null,
      createdAt: response.createdAt
    }
  }

  /**
   * Convert backend FormResponse to frontend FormDetail type
   */
  function mapFormDetailResponse(response: FormResponse): FormDetail {
    return {
      ...mapFormResponse(response),
      items: (response.items ?? []).map(mapFormItemResponse)
    }
  }

  async function fetchForms() {
    loading.value = true
    error.value = null

    try {
      const api = initApiService()
      const response = await api.forms.listForms()
      forms.value = (response.data.forms ?? []).map(mapFormResponse)
      usesForms.value = forms.value.length > 0
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.loadForms'))
      logUnexpectedFailure('Failed to fetch forms:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch whether the organization uses formulieren (has at least one form).
   * Lightweight alternative to fetchForms; requires only `forms.edit`.
   * Fails closed: on error `usesForms` stays null and forms navigation stays hidden.
   */
  async function fetchFormsUsage() {
    try {
      const api = initApiService()
      const response = await api.forms.getFormsUsage()
      usesForms.value = response.data.usesForms
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to fetch forms usage:', err)
    }
  }

  /**
   * Result of delete operation with error details
   */
  type DeleteErrorType = 'not_found' | 'forbidden'
  type DeleteResult =
    | { success: true }
    | { success: false; errorType: DeleteErrorType | 'network' | 'unknown'; message: string }

  async function deleteForm(formId: string): Promise<DeleteResult> {
    try {
      const api = initApiService()
      await api.forms.deleteForm(formId)
      forms.value = forms.value.filter(f => f.id !== formId)
      usesForms.value = forms.value.length > 0
      return { success: true }
    } catch (err: unknown) {
      const result = handleApiError<DeleteErrorType>(err, {
        404: { type: 'not_found', message: 'Het formulier bestaat niet meer. Mogelijk is deze al verwijderd.' },
        403: { type: 'forbidden', message: 'U heeft geen toestemming om dit formulier te verwijderen.' }
      }, t('errors.deleteForm'))

      logUnhandledApiFailure(result, 'Failed to delete form:', err)
      return result
    }
  }

  async function createForm(data: FormMetadata): Promise<Form | null> {
    try {
      const api = initApiService()
      const request: CreateFormRequest = {
        title: data.title,
        description: data.description,
        aiInstruction: data.aiInstruction,
        forAppointment: data.forAppointment,
        forClient: data.forClient,
        includeInAiRecap: data.includeInAiRecap
      }
      const response = await api.forms.createForm(request)
      const newForm = mapFormResponse(response.data)
      forms.value = [newForm, ...forms.value]
      usesForms.value = true
      return newForm
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to create form:', err)
      throw err
    }
  }

  async function updateForm(formId: string, data: FormMetadata): Promise<Form | null> {
    try {
      const api = initApiService()
      const request: UpdateFormRequest = {
        title: data.title,
        description: data.description,
        aiInstruction: data.aiInstruction,
        forAppointment: data.forAppointment,
        forClient: data.forClient,
        includeInAiRecap: data.includeInAiRecap
      }
      const response = await api.forms.updateForm(formId, request)
      const updatedForm = mapFormResponse(response.data)
      forms.value = forms.value.map(f =>
        f.id === formId ? updatedForm : f
      )
      return updatedForm
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to update form:', err)
      throw err
    }
  }

  async function activateForm(formId: string): Promise<Form | null> {
    try {
      const api = initApiService()
      const response = await api.forms.activateForm(formId)
      const updatedForm = mapFormResponse(response.data)
      forms.value = forms.value.map(f =>
        f.id === formId ? updatedForm : f
      )
      return updatedForm
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to activate form:', err)
      throw err
    }
  }

  async function deprecateForm(formId: string): Promise<Form | null> {
    try {
      const api = initApiService()
      const response = await api.forms.deprecateForm(formId)
      const updatedForm = mapFormResponse(response.data)
      forms.value = forms.value.map(f =>
        f.id === formId ? updatedForm : f
      )
      return updatedForm
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to deprecate form:', err)
      throw err
    }
  }

  async function reactivateForm(formId: string): Promise<Form | null> {
    try {
      const api = initApiService()
      const response = await api.forms.reactivateForm(formId)
      const updatedForm = mapFormResponse(response.data)
      forms.value = forms.value.map(f =>
        f.id === formId ? updatedForm : f
      )
      return updatedForm
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to reactivate form:', err)
      throw err
    }
  }

  /**
   * Forms filtered by status
   */
  const draftForms = computed(() => forms.value.filter(f => f.status === 'DRAFT'))
  const activeForms = computed(() => forms.value.filter(f => f.status === 'ACTIVE'))
  const deprecatedForms = computed(() => forms.value.filter(f => f.status === 'DEPRECATED'))

  /**
   * Fetch a single form with all its items
   */
  async function fetchForm(formId: string): Promise<FormDetail> {
    try {
      const api = initApiService()
      const response = await api.forms.getForm(formId)
      return mapFormDetailResponse(response.data)
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to fetch form:', err)
      throw err
    }
  }

  /**
   * Add a question item to a form
   */
  async function addQuestionItem(
    formId: string,
    questionId: string,
    position: number
  ): Promise<FormItem | null> {
    try {
      const api = initApiService()
      const request: AddFormItemRequest = {
        itemType: 'QUESTION',
        position,
        questionId
      }
      const response = await api.formItems.addFormItem(formId, request)
      return mapFormItemResponse(response.data)
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to add question item:', err)
      throw err
    }
  }

  /**
   * Add a static text item to a form
   */
  async function addStaticTextItem(
    formId: string,
    text: string,
    position: number
  ): Promise<FormItem | null> {
    try {
      const api = initApiService()
      const request: AddFormItemRequest = {
        itemType: 'STATIC_TEXT',
        position,
        content: { text }
      }
      const response = await api.formItems.addFormItem(formId, request)
      return mapFormItemResponse(response.data)
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to add static text item:', err)
      throw err
    }
  }

  /**
   * Remove an item from a form
   */
  async function removeFormItem(formId: string, itemId: string): Promise<boolean> {
    try {
      const api = initApiService()
      await api.formItems.deleteFormItem(formId, itemId)
      return true
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to remove form item:', err)
      throw err
    }
  }

  /**
   * Reorder form items
   */
  async function reorderFormItems(formId: string, itemIds: string[]): Promise<FormDetail | null> {
    try {
      const api = initApiService()
      const request: ReorderItemsRequest = { itemIds }
      const response = await api.formItems.reorderFormItems(formId, request)
      return mapFormDetailResponse(response.data)
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to reorder form items:', err)
      throw err
    }
  }

  /**
   * Duplicate a form item
   */
  async function duplicateFormItem(formId: string, itemId: string): Promise<FormDetail | null> {
    try {
      const api = initApiService()
      const response = await api.formItems.duplicateFormItem(formId, itemId)
      return mapFormDetailResponse(response.data)
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to duplicate form item:', err)
      throw err
    }
  }

  return {
    forms,
    loading,
    error,
    usesForms,
    draftForms,
    activeForms,
    deprecatedForms,
    fetchForms,
    fetchFormsUsage,
    fetchForm,
    createForm,
    updateForm,
    deleteForm,
    activateForm,
    deprecateForm,
    reactivateForm,
    addQuestionItem,
    addStaticTextItem,
    removeFormItem,
    reorderFormItems,
    duplicateFormItem
  }
})
